import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, XCircle, Send, ShoppingCart } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';
import CompanySelector from '../../../../components/admin/procurement/CompanySelector';
import BankAccountSelector from '../../../../components/admin/procurement/BankAccountSelector';
import SupplierSelector from '../../../../components/admin/procurement/SupplierSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  converted_to_po: { label: 'Converted to PO', color: 'bg-purple-100 text-purple-800' },
};

const PurchaseRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [conversionData, setConversionData] = useState({
    supplier_id: '',
    company_id: '',
    bank_account_id: '',
  });
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchPR();
  }, [id]);

  const fetchPR = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/procurement/purchase-requests/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch PR');
      const data = await response.json();
      setPr(data);
    } catch (error) {
      console.error('Error fetching PR:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchase request',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowAction = async (action, requiresComment = false) => {
    try {
      let comments = null;
      if (requiresComment) {
        comments = prompt(`Please provide ${action === 'reject' ? 'rejection' : ''} comments:`);
        if (!comments) return;
      }

      const response = await fetch(`${API_URL}/api/procurement/purchase-requests/${id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(comments ? { comments } : {})
      });

      if (!response.ok) throw new Error(`Failed to ${action}`);

      toast({
        title: 'Success',
        description: `PR ${action} successfully`
      });

      fetchPR();
    } catch (error) {
      console.error(`Error ${action}:`, error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleConvertToPO = async () => {
    try {
      if (!conversionData.supplier_id || !conversionData.company_id || !conversionData.bank_account_id) {
        toast({
          title: 'Validation Error',
          description: 'Please select supplier, company, and bank account',
          variant: 'destructive'
        });
        return;
      }

      const response = await fetch(`${API_URL}/api/procurement/purchase-requests/${id}/convert-to-po`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(conversionData)
      });

      if (!response.ok) throw new Error('Failed to convert to PO');
      const result = await response.json();

      toast({
        title: 'Success',
        description: `PR converted to ${result.po_number}`
      });

      navigate(`/admin/procurement/purchase-orders/${result.po_id}`);
    } catch (error) {
      console.error('Error converting to PO:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div>
        <BackButton />
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading purchase request...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pr) {
    return (
      <div>
        <BackButton />
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Purchase request not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <BackButton />

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <FileText className="h-8 w-8 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">{pr.pr_number}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <Badge className={STATUS_CONFIG[pr.status]?.color}>
                  {STATUS_CONFIG[pr.status]?.label}
                </Badge>
                <span className="text-sm text-[#6C757D]">{formatDate(pr.created_at)}</span>
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            {pr.status === 'draft' && (
              <Button onClick={() => handleWorkflowAction('submit')} className="bg-[#00B4D8]">
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}
            {pr.status === 'submitted' && (
              <>
                <Button onClick={() => handleWorkflowAction('review')} className="bg-yellow-600">
                  Start Review
                </Button>
                <Button onClick={() => handleWorkflowAction('reject', true)} variant="outline" className="border-red-500 text-red-500">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
            {pr.status === 'under_review' && (
              <>
                <Button onClick={() => handleWorkflowAction('approve')} className="bg-green-600">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button onClick={() => handleWorkflowAction('reject', true)} variant="outline" className="border-red-500 text-red-500">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
            {pr.status === 'approved' && !pr.converted_to_po_id && (
              <Button onClick={() => setShowConvertModal(true)} className="bg-purple-600">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Convert to PO
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-[#2B2D42] mb-2">Requester</h3>
            <p className="text-sm text-[#2B2D42] font-medium">{pr.requester_name}</p>
            <p className="text-xs text-[#6C757D]">{pr.requester_email}</p>
            <p className="text-xs text-[#6C757D] mt-1">Department: {pr.department}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-[#2B2D42] mb-2">Details</h3>
            <p className="text-sm text-[#6C757D]">Urgency: <span className="font-medium text-[#2B2D42]">{pr.urgency_level}</span></p>
            <p className="text-sm text-[#6C757D]">Currency: <span className="font-medium text-[#2B2D42]">{pr.currency}</span></p>
            {pr.budget_code && (
              <p className="text-sm text-[#6C757D]">Budget Code: <span className="font-medium text-[#2B2D42]">{pr.budget_code}</span></p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-[#2B2D42] mb-2">Total</h3>
            <p className="text-2xl font-bold text-[#00B4D8]">
              {formatCurrency(pr.estimated_total, pr.currency)}
            </p>
            <p className="text-xs text-[#6C757D] mt-1">{pr.items?.length} items</p>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">{pr.title}</h3>
          <p className="text-sm text-[#6C757D] mb-4">{pr.justification}</p>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Items</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-sm font-medium text-[#6C757D] pb-3">Product</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Qty</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Est. Unit Price</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {pr.items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 text-sm text-[#2B2D42]">{item.product_name}</td>
                    <td className="py-3 text-sm text-[#2B2D42] text-right">{item.quantity} {item.unit}</td>
                    <td className="py-3 text-sm text-[#2B2D42] text-right">
                      {formatCurrency(item.estimated_unit_price, pr.currency)}
                    </td>
                    <td className="py-3 text-sm text-[#2B2D42] font-medium text-right">
                      {formatCurrency(item.estimated_total, pr.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showConvertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-[#2B2D42] mb-6">Convert to Purchase Order</h3>
              
              <div className="space-y-6">
                <SupplierSelector
                  value={conversionData.supplier_id}
                  onChange={(id) => setConversionData({ ...conversionData, supplier_id: id })}
                  required
                />
                
                <CompanySelector
                  value={conversionData.company_id}
                  onChange={(id) => setConversionData({ ...conversionData, company_id: id })}
                  required
                />
                
                <BankAccountSelector
                  companyId={conversionData.company_id}
                  value={conversionData.bank_account_id}
                  onChange={(id) => setConversionData({ ...conversionData, bank_account_id: id })}
                  required
                />
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <Button onClick={() => setShowConvertModal(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleConvertToPO} className="bg-purple-600">
                  Create Purchase Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PurchaseRequestDetails;